import { COOKIE_KEY, COOKIE_OPTIONS } from '@/lib/Constants';
import { encryptAES } from '@/lib/crypto/AES';
import { Endpoints } from '@/lib/Endpoints';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import { parseExperiments, serializeExperiments, type AbExperimentMap } from '@/lib/abExperiments';
import type { NextResponse } from 'next/server';

export type AnonymousSession = {
  token: string;
  deviceId: string;
  experiments: AbExperimentMap;
};

async function postInit(initBody: Record<string, unknown>): Promise<any> {
  const url = `${getApiBaseUrl()}${Endpoints.INIT}`;
  // Next/undici `fetch` can refuse RFC1918 (internal ALB 10.x). Axios is used
  // on the server so Fargate SSR can INIT against services-test:3091.
  if (typeof window === 'undefined') {
    const axios = (await import('axios')).default;
    const { data } = await axios.post(url, initBody, {
      timeout: 8000,
      headers: { 'Content-Type': 'application/json' },
      validateStatus: () => true,
    });
    return data;
  }
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(initBody),
  });
  if (!response.ok) return null;
  return response.json().catch(() => null);
}

export async function createAnonymousSession(opts?: {
  osType?: string;
  deviceId?: string;
  invitationCode?: string;
}): Promise<AnonymousSession | null> {
  const initBody = {
    device: {
      osType: opts?.osType || 'web',
      ...(opts?.deviceId ? { deviceId: opts.deviceId } : {}),
    },
    invitationCode: opts?.invitationCode,
  };

  try {
    const result = await postInit(initBody);
    const token = result?.data?.token;
    const deviceId = result?.data?.deviceId;
    if (!token) return null;
    return {
      token,
      deviceId: opts?.deviceId || deviceId,
      experiments: parseExperiments(result?.data?.experiments),
    };
  } catch {
    return null;
  }
}

export function applySessionCookies(
  res: NextResponse,
  session: AnonymousSession,
  extra?: { invitationCode?: string },
) {
  res.cookies.set(COOKIE_KEY.TOKEN, encryptAES(session.token), { ...COOKIE_OPTIONS });
  if (session.deviceId) {
    res.cookies.set(COOKIE_KEY.DEVICE_ID, encryptAES(session.deviceId), { ...COOKIE_OPTIONS });
  }
  res.cookies.set(COOKIE_KEY.AB, serializeExperiments(session.experiments || {}), { ...COOKIE_OPTIONS });
  if (extra?.invitationCode) {
    res.cookies.set(COOKIE_KEY.REFERRER_ID, extra.invitationCode, { ...COOKIE_OPTIONS });
  }
  return res;
}
